import { SheetClip } from "../external/sheetclip";
import { TimesheetSelectedCells } from "./timesheet-selected-cells";
import { TimesheetRightTableDataManager } from "./timesheet-right-table-data-manager";
import { MessageDisplayer } from "../common/message-displayer";
import { Txt } from "../common/text";

export class TimesheetCopyPasteManager
{
    private hot?: Handsontable;
    private container?: HTMLElement;
    private copyData?: string[][][];
    private copyText?: string[][];

    private readonly boundCopyHandler = (event: ClipboardEvent) => this.copyFromEvent(event);
    private readonly boundPasteHandler = async (event: ClipboardEvent) => await this.pasteFromEvent(event);

    constructor(private readonly dataManager: TimesheetRightTableDataManager)
    {
        document.addEventListener("copy", this.boundCopyHandler, true);
        document.addEventListener("paste", this.boundPasteHandler, true);
    }

    attach(container: HTMLElement, hot: Handsontable): void
    {
        this.container = container;
        this.hot = hot;
    }

    destroy(): void
    {
        document.removeEventListener("copy", this.boundCopyHandler, true);
        document.removeEventListener("paste", this.boundPasteHandler, true);
        this.hot = undefined;
        this.container = undefined;
    }

    async copyFromButton(selectedCells: TimesheetSelectedCells): Promise<void>
    {
        [this.copyData, this.copyText] = this.getCopyData(selectedCells);
        try
        {
            const text = SheetClip.stringify(this.copyText);
            await navigator.clipboard.writeText(text);
        }
        catch (error)
        {
            // Silently fail if clipboard access is denied
            console.error("Clipboard write failed: ", error);
        }
    }

    private copyFromEvent(event: ClipboardEvent): void
    {
        const selectedCells = this.getSelectedCells(event);
        if (!selectedCells)
        {
            return;
        }
        [this.copyData, this.copyText] = this.getCopyData(selectedCells);
    }

    async pasteFromButton(selectedCells: TimesheetSelectedCells): Promise<void>
    {
        let pastedText: string;
        try
        {
            pastedText = await navigator.clipboard.readText();
        }
        catch (error)
        {
            MessageDisplayer.showError(Txt.clipboardAccessDenied);
            return;
        }
        if (this.doesTextMatchCopyData(pastedText))
        {
            await this.dataManager.copy(this.copyData, selectedCells);
        }
        else
        {
            this.pasteRawChanges(SheetClip.parse(pastedText), selectedCells);
        }
    }

    private async pasteFromEvent(event: ClipboardEvent): Promise<void>
    {
        const selectedCells = this.getSelectedCells(event);
        if (!selectedCells)
        {
            return;
        }
        const pastedText = event.clipboardData?.getData("text/plain") ?? "";
        if (this.doesTextMatchCopyData(pastedText))
        {
            event.preventDefault();
            event.stopPropagation();
            await this.dataManager.copy(this.copyData, selectedCells);
        }
    }

    private doesTextMatchCopyData(text: string): boolean
    {
        const normalizedText = SheetClip.stringify(SheetClip.parse(text.replace(/\r\n/g, "\n")));
        return this.copyText && SheetClip.stringify(this.copyText) === normalizedText;
    }

    private getSelectedCells(event: ClipboardEvent): TimesheetSelectedCells
    {
        const eventElement = event.target instanceof HTMLElement ? event.target : null;
        if (this.hot && eventElement && this.container && this.container.contains(eventElement))
        {
            return TimesheetSelectedCells.get(this.hot);
        }
        return null;
    }

    private pasteRawChanges(grid: string[][], selectedCells: TimesheetSelectedCells): void
    {
        const changes: [number, number, string][] = [];
        grid.forEach((rowData, rowIndex) =>
        {
            rowData.forEach((value, colIndex) =>
            {
                const r = selectedCells.startRow + rowIndex;
                const c = selectedCells.startColumn + colIndex;
                if (this.dataManager.isCellInRange(r, c) && this.dataManager.isCellEditable(r, c))
                {
                    changes.push([r, c, value]);
                }
            });
        });

        if (changes.length > 0)
        {
            this.hot.setDataAtCell(changes);
        }
    }

    copyFromAutofill(start: { row: number; col: number }, end: { row: number; col: number }): void
    {
        if (!this.hot || !start || !end)
        {
            return;
        }
        const selectedCells = TimesheetSelectedCells.get(this.hot);
        if (!selectedCells)
        {
            return;
        }
        const targetCells = new TimesheetSelectedCells();
        targetCells.startRow = Math.min(start.row, end.row);
        targetCells.endRow = Math.max(start.row, end.row);
        targetCells.startColumn = Math.min(start.col, end.col);
        targetCells.endColumn = Math.max(start.col, end.col);
        this.dataManager.copy(this.getCopyData(selectedCells)[0], targetCells)
            .catch(error => console.error("Autofill copy failed: ", error));
    }

    private getCopyData(selectedCells: TimesheetSelectedCells): [string[][][], string[][]]
    {
        const data = this.dataManager.getData();
        const copyData: string[][][] = [];
        const copyText: string[][] = [];
        for (let r = selectedCells.startRow; r <= selectedCells.endRow; r++)
        {
            const dataRow: string[][] = [];
            const textRow: string[] = [];
            for (let c = selectedCells.startColumn; c <= selectedCells.endColumn; c++)
            {
                const cell = data.getCell(r, c);
                dataRow.push(cell.CopyValue);
                textRow.push(cell.EditValue);
            }
            copyData.push(dataRow);
            copyText.push(textRow);
        }
        return [copyData, copyText];
    }
}
